import { Alert, Button, Divider, Stack, TextField, Typography } from "@mui/material";
import { omit } from "lodash";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { AutocompleteModel, GqlUserUpdateInput } from "~/__generated__";
import GaiaAutocompleteOptionsDropdown from "~/modules/app/components/Autocomplete/GaiaAutocompleteOptionsDropdown";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

import { ME_GET_USER_INFO } from "../../../auth/api/useMeGetUserInfoQuery";
import { USER_GET_BY_ID } from "../../api/useUserGetById";
import useUserUpdate from "../../api/useUserUpdate";
import { USERS_GET_ALL } from "../../screens/UserListScreen/api/useUserListScreen_GetAll";
import PhoneNumberField from "../PhoneNumberField";
import ProfilePhotoUploader from "../ProfilePhotoUploader";

type UserUpdateFormData = {
    id: string;
    firstName: string;
    lastName: string;
    phone?: string | null;
    email: string;
    externalReference?: string | null;
    tenantRole?: {
        id: string;
    } | null;
    profilePictureMedia?: {
        url?: string | null;
    } | null;
};

type Props = {
    user: UserUpdateFormData;
    onSubmitted: () => void;
};

type UserUpdateFormValues = Omit<GqlUserUpdateInput, "userId">;

export default function UserUpdateForm({ user, onSubmitted }: Props) {
    const { t } = useTranslation("user");
    const { updateUser, error } = useUserUpdate(user.id, [USER_GET_BY_ID, ME_GET_USER_INFO, USERS_GET_ALL]);
    const {
        handleSubmit,
        register,
        setError,
        control,
        getFieldState,
        formState: { errors, isLoading, isDirty, isSubmitted, isSubmitting },
    } = useForm<UserUpdateFormValues>({
        values: {
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            phone: user.phone,
            roleId: user.tenantRole?.id || "",
            externalReference: user.externalReference,
        },
    });

    const onSubmit: SubmitHandler<UserUpdateFormValues> = async data => {
        const { isDirty: isPhoneDirty } = getFieldState("phone");
        await updateUser(isPhoneDirty ? data : omit(data, "phone"));
        onSubmitted();
    };

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}

                <ProfilePhotoUploader user={user} />

                <TextField
                    label={t("userForm.labelFirstname")}
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    {...register("firstName")}
                />

                <TextField
                    label={t("userForm.labelLastname")}
                    size="small"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    {...register("lastName")}
                />

                <Controller
                    name="roleId"
                    control={control}
                    render={({ field }) => (
                        <GaiaAutocompleteOptionsDropdown
                            sx={{ mt: 2 }}
                            model={AutocompleteModel.TenantRole}
                            label={t("userForm.labelRole")}
                            error={!!errors.roleId}
                            helperText={errors.roleId?.message}
                            value={field.value || ""}
                            onChange={field.onChange}
                        />
                    )}
                />

                <TextField
                    label={t("userForm.labelExternalReference")}
                    size="small"
                    error={!!errors.externalReference}
                    helperText={errors.externalReference?.message}
                    {...register("externalReference")}
                />

                <Divider>
                    <Typography variant="caption" textTransform="uppercase">
                        {t("userForm.sectionContact")}
                    </Typography>
                </Divider>

                <TextField
                    label={t("userForm.labelEmail")}
                    size="small"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    {...register("email")}
                />

                <PhoneNumberField<UserUpdateFormValues> control={control} name="phone" required />

                <Button
                    sx={{ mt: 3 }}
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={!isDirty}
                    loading={isLoading || isSubmitting}
                >
                    {t("userForm.btnSubmit")}
                </Button>
            </Stack>
        </form>
    );
}
